import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Shield, Zap, Factory, BookOpen, Target, ChevronRight, ChevronLeft, ZoomIn, X, BadgeCheck } from "lucide-react";
import SistemaConfigurador from "@/components/SistemaConfigurador";
import CartelaCores from "@/components/CartelaCores";
import CoberturaNacional from "@/components/CoberturaNacional";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { faqData } from "@/data/faqData";
import Layout from "@/components/Layout";
import SEOMeta from "@/components/SEOMeta";
import { Helmet } from "react-helmet-async";
import GallerySection from "@/components/GallerySection";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.25, 0.1, 0.25, 1] as const } },
};

const stagger = {
  visible: { transition: { staggerChildren: 0.1 } },
};

/* ─── HERO ─── */
const HeroSection = () => (
  <section className="relative overflow-hidden bg-foreground">
    <div className="absolute inset-0">
      <picture>
        <source media="(min-width:768px)" srcSet="/hero-desktop.webp" />
        <img
          src="/hero-mobile.webp"
          alt="Balde de tinta Indusport para pisos esportivos"
          decoding="async"
          className="h-full w-full object-cover opacity-50"
          style={{ objectPosition: "center 65%" }}
        />
      </picture>
      <div className="absolute inset-0 bg-gradient-to-r from-foreground via-foreground/85 to-foreground/30" />
    </div>
    <div className="container relative z-10 flex min-h-[85vh] items-center py-20 md:py-32">
      <motion.div initial="hidden" animate="visible" variants={stagger} className="max-w-2xl">
        <motion.span variants={fadeUp} className="mb-4 inline-block font-mono text-xs uppercase tracking-[0.2em] text-accent">
          Fábrica Especialista
        </motion.span>
        <motion.h1 variants={fadeUp} className="text-3xl sm:text-4xl font-extrabold leading-[1.1] tracking-tighter text-background md:text-6xl lg:text-7xl">
          Fábrica especialista em tintas para pisos esportivos.
        </motion.h1>
        <motion.p variants={fadeUp} className="mt-6 max-w-[52ch] text-lg leading-relaxed text-background/70">
          A Indusport desenvolve e fabrica tintas, resinas e sistemas de pintura técnicos para quadras e superfícies esportivas, com foco em aderência, durabilidade, preparo correto da base e redução de retrabalho.
        </motion.p>
        <motion.div variants={fadeUp} className="mt-8 flex flex-wrap gap-4">
          <Link to="/contato">
            <Button variant="cta" size="xl">
              Solicitar Orçamento
              <ArrowRight className="h-5 w-5" />
            </Button>
          </Link>
          <Link to="/produtos">
            <Button variant="cta-outline" size="xl" className="border-background/30 text-background hover:bg-background hover:text-foreground">
              Ver Produtos
              <ArrowRight className="h-5 w-5" />
            </Button>
          </Link>
        </motion.div>
      </motion.div>
    </div>
  </section>
);

/* ─── POSICIONAMENTO ─── */
const PositionSection = () => (
  <section className="bg-primary-gradient py-20 md:py-28">
    <div className="container">
      <motion.div
        initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }}
        variants={fadeUp}
        className="mx-auto max-w-4xl text-center"
      >
        <span className="mb-4 inline-block font-mono text-xs uppercase tracking-[0.2em] text-primary-foreground/50">
          Posicionamento
        </span>
        <p className="text-2xl font-bold leading-relaxed text-primary-foreground md:text-3xl lg:text-4xl tracking-tight">
          Mais do que fornecer tinta, a Indusport fabrica{" "}
          <span className="text-accent">sistemas de pintura técnicos</span> para superfícies esportivas.
        </p>
        <p className="mt-6 text-base text-primary-foreground/70 max-w-[60ch] mx-auto leading-relaxed">
          O resultado final depende da escolha correta dos materiais, da preparação da base e da compatibilidade entre as etapas do sistema de pintura.
        </p>
      </motion.div>
    </div>
  </section>
);

/* ─── COBERTURA NACIONAL (ver CoberturaNacional.tsx) ─── */

/* ─── PRODUTOS (ver SistemaConfigurador) ─── */

/* ─── SEGMENTOS ─── */
import { segmentos as segmentosData } from "@/data/segmentos";

const SegmentsSection = () => (
  <section className="py-20 md:py-28">
    <div className="container">
      <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={stagger}>
        <motion.div variants={fadeUp} className="mb-12 max-w-2xl">
          <span className="mb-3 inline-block font-mono text-xs uppercase tracking-[0.2em] text-accent">Segmentos</span>
          <h2 className="text-3xl font-extrabold tracking-tighter md:text-4xl">
            Soluções para quem constrói, reforma e mantém quadras esportivas.
          </h2>
          <p className="mt-4 text-muted-foreground leading-relaxed">
            Selecione seu perfil para ver a página dedicada do seu segmento.
          </p>
        </motion.div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {segmentosData.map((s) => (
            <motion.div key={s.slug} variants={fadeUp}>
              <Link
                to={`/segmentos/${s.slug}`}
                className="group flex h-full items-start gap-4 border border-border bg-card p-6 transition-all hover:border-primary hover:shadow-card-hover"
              >
                <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                  <s.icon className="h-6 w-6" />
                </div>
                <div>
                  <h3 className="font-bold leading-tight">{s.title}</h3>
                  <span className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-primary">
                    Ver soluções <ChevronRight className="h-3 w-3" />
                  </span>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </div>
  </section>
);

/* ─── DIFERENCIAIS ─── */
const diffs = [
  { icon: Factory, text: "Fábrica especialista em pisos esportivos" },
  { icon: Target, text: "Foco técnico, não genérico" },
  { icon: BookOpen, text: "Orientação para escolha correta do sistema de pintura" },
  { icon: Shield, text: "Redução de retrabalho e falhas" },
  { icon: Zap, text: "Portfólio voltado à durabilidade e aderência" },
];

const DiffsSection = () => (
  <section className="bg-primary-gradient py-20 md:py-28">
    <div className="container">
      <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={stagger}>
        <motion.div variants={fadeUp} className="mb-12 max-w-2xl">
          <span className="mb-3 inline-block font-mono text-xs uppercase tracking-[0.2em] text-primary-foreground/50">Diferenciais</span>
          <h2 className="text-3xl font-extrabold tracking-tighter text-primary-foreground md:text-4xl">
            Por que a Indusport é diferente.
          </h2>
        </motion.div>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-5">
          {diffs.map((d) => (
            <motion.div key={d.text} variants={fadeUp} className="flex flex-col items-start gap-3 border border-primary-foreground/10 p-6 rounded-sm">
              <d.icon className="h-8 w-8 text-accent" />
              <p className="text-sm font-semibold text-primary-foreground leading-snug">{d.text}</p>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </div>
  </section>
);

/* ─── EDUCATIVO removido — Central Técnica substituída pela nova FAQSection ─── */


/* ─── FAQ ─── */
const homeFaqItems = [0, 1, 3, 4, 5, 6].map((i) => faqData[i]);

const FAQSection = () => (
  <section className="bg-surface-muted py-20 md:py-28">
    <div className="container max-w-[1120px]">
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={stagger}
      >
        <div className="grid items-start gap-9 lg:grid-cols-[0.82fr_1.18fr] lg:gap-14">
          {/* Aside sticky */}
          <motion.div variants={fadeUp} className="lg:sticky lg:top-10">
            <span className="font-mono text-[0.72rem] uppercase tracking-[0.2em] text-accent">FAQ</span>
            <h2 className="mt-4 text-[1.85rem] font-extrabold leading-[1.08] tracking-[-0.045em] sm:text-[2.3rem]">
              Dúvidas frequentes
            </h2>
            <p className="mt-4 max-w-[34ch] leading-relaxed text-muted-foreground">
              Respostas diretas sobre sistema de pintura, preparo de base e orientação técnica.
            </p>
            <div className="mt-[26px] flex flex-col items-start gap-3">
              <Link to="/faq">
                <Button variant="cta" size="lg">
                  Ver todas as perguntas <ArrowRight className="h-4 w-4" />
                </Button>
              </Link>
              <Link to="/contato">
                <Button variant="cta-outline" size="lg">
                  Falar com consultor técnico
                </Button>
              </Link>
            </div>
            <div className="mt-[22px] flex items-center gap-[10px] text-[0.84rem] text-muted-foreground">
              <BadgeCheck className="h-4 w-4 shrink-0 text-primary" />
              Orientação técnica sem custo
            </div>
          </motion.div>

          {/* Accordion */}
          <motion.div variants={fadeUp}>
            <Accordion type="single" collapsible defaultValue="item-0">
              {homeFaqItems.map((item, i) => (
                <AccordionItem
                  key={i}
                  value={`item-${i}`}
                  className="mt-3 border border-border bg-card transition-colors first:mt-0 hover:border-primary data-[state=open]:border-primary"
                >
                  <AccordionTrigger className="px-6 py-[22px] text-left text-[1rem] font-bold leading-[1.3] hover:no-underline [&[data-state=open]>svg]:text-primary">
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="border-t border-border px-6 pb-[22px] pt-4 text-[0.92rem] leading-[1.6] text-muted-foreground">
                    {item.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        </div>
      </motion.div>
    </div>
  </section>
);

/* ─── HOME ─── */
const orgSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Indusport",
  description: "Fábrica especialista em tintas, resinas e sistemas de pintura para pisos esportivos",
  url: "https://www.indusport.com.br",
  logo: "https://www.indusport.com.br/favicon.png",
  address: {
    "@type": "PostalAddress",
    addressLocality: "São José do Rio Preto",
    addressRegion: "SP",
    addressCountry: "BR",
  },
  contactPoint: {
    "@type": "ContactPoint",
    telephone: "+55-17-98139-0073",
    contactType: "customer service",
    areaServed: "BR",
    availableLanguage: "Portuguese",
  },
  sameAs: [
    "https://www.instagram.com/indusportriopreto/",
    "https://www.tiktok.com/@indusportbr",
    "https://www.youtube.com/@Indusport",
  ],
};

const Index = () => (
  <Layout>
    <SEOMeta
      title="Indusport — Fábrica de Tintas e Sistemas para Pisos Esportivos"
      description="A Indusport fabrica tintas, resinas e sistemas de pintura para quadras esportivas. Soluções para construção, recuperação e pintura de pisos em todo o Brasil."
      canonical="/"
    />
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
    </Helmet>
    <HeroSection />
    <GallerySection />
    <CoberturaNacional />
    <SistemaConfigurador />
    <CartelaCores />
    <PositionSection />
    <SegmentsSection />
    <DiffsSection />
    <FAQSection />
  </Layout>
);

export default Index;
